import dayjs from 'dayjs';
import z from 'zod';
import { HumanFullNameSchema, UnixTimestampSchema, UUIDSchema } from '../common/schemas';
import {
  DBLegacyExamExamIdSchema,
  DBLegacyExamLabelSchema,
  DBLegacyExamSubjectSchema,
} from '../legacyExams/schemas';
import { DBStaffStaffIdSchema } from '../staff/schemas';
import { DBStudentStudentIdSchema } from '../students/schemas';
import { LegacyExamRequestStatus } from './enums';

/**
 * Shared & Helper Schemas
 */

/**
 * DB Schemas
 */

export const DBLegacyExamMarkingRequestRequestIdSchema = UUIDSchema;
export const DBLegacyExamMarkingRequestCreationDatetimeSchema = UnixTimestampSchema;
export const DBLegacyExamMarkingRequestDueDatetimeSchema = UnixTimestampSchema;
export const DBLegacyExamMarkingRequestExamIdSchema = z.union([
  z.lazy(() => DBLegacyExamExamIdSchema),
  z.literal(''),
]);
export const DBLegacyExamMarkingRequestExamLabelSchema = z.union([
  z.lazy(() => DBLegacyExamLabelSchema),
  z.literal(''),
]);
export const DBLegacyExamMarkingRequestExamSubjectSchema = z.union([
  z.lazy(() => DBLegacyExamSubjectSchema),
  z.literal(''),
]);
export const DBLegacyExamMarkingRequestFileNameSchema = z.string().max(2048);
export const DBLegacyExamMarkingRequestMarkerSchema = z.lazy(() => DBStaffStaffIdSchema);
export const DBLegacyExamMarkingRequestMarkerNameSchema = HumanFullNameSchema;
export const DBLegacyExamMarkingRequestResponsesSchema = z
  .record(z.string(), z.record(z.string().length(1), z.boolean()))
  .openapi({
    description:
      'Automatically generated mapping of automatically read responses for multiple choice questions',
    example: {
      '1': { A: true, B: false, C: false, D: false },
      '2': { A: false, B: true, C: false, D: false },
      '3': { A: false, B: false, C: true, D: false },
      '4': { A: false, B: false, C: false, D: true },
      '5': { A: true, B: true, C: false, D: false },
    },
  });
export const DBLegacyExamMarkingRequestStatusSchema = z.enum(LegacyExamRequestStatus);
export const DBLegacyExamMarkingRequestStudentSchema = z.union([
  z.lazy(() => DBStudentStudentIdSchema),
  z.literal(''),
]);
export const DBLegacyExamMarkingRequestStudentNameSchema = z.union([
  HumanFullNameSchema,
  z.literal(''),
]);

export const DBLegacyExamMarkingRequestSchema = z.object({
  requestId: DBLegacyExamMarkingRequestRequestIdSchema,
  creationDatetime: DBLegacyExamMarkingRequestCreationDatetimeSchema,
  dueDatetime: DBLegacyExamMarkingRequestDueDatetimeSchema,
  examId: DBLegacyExamMarkingRequestExamIdSchema.optional(),
  examLabel: DBLegacyExamMarkingRequestExamLabelSchema.optional(),
  examSubject: DBLegacyExamMarkingRequestExamSubjectSchema.optional(),
  fileName: DBLegacyExamMarkingRequestFileNameSchema,
  marker: DBLegacyExamMarkingRequestMarkerSchema.optional(),
  markerName: DBLegacyExamMarkingRequestMarkerNameSchema.optional(),
  responses: DBLegacyExamMarkingRequestResponsesSchema,
  status: DBLegacyExamMarkingRequestStatusSchema,
  student: DBLegacyExamMarkingRequestStudentSchema.optional(),
  studentName: DBLegacyExamMarkingRequestStudentNameSchema.optional(),
});

/**
 * Client Schemas
 */

export const LegacyExamMarkingRequestRequestIdSchema = DBLegacyExamMarkingRequestRequestIdSchema;
export const LegacyExamMarkingRequestCreationDatetimeSchema =
  DBLegacyExamMarkingRequestCreationDatetimeSchema;
export const LegacyExamMarkingRequestDueDatetimeSchema =
  DBLegacyExamMarkingRequestDueDatetimeSchema;
export const LegacyExamMarkingRequestExamIdSchema = DBLegacyExamMarkingRequestExamIdSchema;
export const LegacyExamMarkingRequestExamLabelSchema = DBLegacyExamMarkingRequestExamLabelSchema;
export const LegacyExamMarkingRequestExamSubjectSchema =
  DBLegacyExamMarkingRequestExamSubjectSchema;
export const LegacyExamMarkingRequestFileNameSchema = DBLegacyExamMarkingRequestFileNameSchema;
export const LegacyExamMarkingRequestMarkerSchema = DBLegacyExamMarkingRequestMarkerSchema;
export const LegacyExamMarkingRequestMarkerNameSchema = DBLegacyExamMarkingRequestMarkerNameSchema;
export const LegacyExamMarkingRequestResponsesSchema = DBLegacyExamMarkingRequestResponsesSchema;
export const LegacyExamMarkingRequestStatusSchema = DBLegacyExamMarkingRequestStatusSchema;
export const LegacyExamMarkingRequestStudentSchema = DBLegacyExamMarkingRequestStudentSchema;
export const LegacyExamMarkingRequestStudentNameSchema =
  DBLegacyExamMarkingRequestStudentNameSchema;

export const LegacyExamMarkingRequestSchema = z.object({
  requestId: LegacyExamMarkingRequestRequestIdSchema,
  creationDatetime: LegacyExamMarkingRequestCreationDatetimeSchema.default(() => dayjs().valueOf()),
  dueDatetime: LegacyExamMarkingRequestDueDatetimeSchema.default(() => dayjs().valueOf()),
  examId: LegacyExamMarkingRequestExamIdSchema.optional(),
  examLabel: LegacyExamMarkingRequestExamLabelSchema.optional(),
  examSubject: LegacyExamMarkingRequestExamSubjectSchema.optional(),
  fileName: LegacyExamMarkingRequestFileNameSchema,
  marker: LegacyExamMarkingRequestMarkerSchema.optional(),
  markerName: LegacyExamMarkingRequestMarkerNameSchema.optional(),
  responses: LegacyExamMarkingRequestResponsesSchema.default({}),
  status: LegacyExamMarkingRequestStatusSchema,
  student: LegacyExamMarkingRequestStudentSchema.optional(),
  studentName: LegacyExamMarkingRequestStudentNameSchema.optional(),
});
